import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import useToken from './useToken';

function useProgressRedirect(expectedStep = null) {
  const { token, refreshAccessToken, getValidToken } = useToken();
  const navigate = useNavigate();
  const location = useLocation();

  const isFetched = useRef(false);

  useEffect(() => {
    const fetchProgress = async () => {
      try {
        const authToken = await getValidToken();
        if (!authToken) {
          navigate("/", { replace: true });
          return;
        }

        const res = await fetch("/api"+'/player/progress', {
          headers: { Authorization: `Bearer ${authToken}` },
        });

        if (!res.ok) return;

        const data = await res.json();
        const currentStep = data.progress_step;

        if (!currentStep) return;

        // Only redirect if expectedStep is set and doesn't match
        if (expectedStep && currentStep !== expectedStep) {
          navigate(getRouteForStep(currentStep), { replace: true });
          window.location.reload();
        }

        // Optional: if expectedStep is not passed, always redirect based on progress
        if (!expectedStep) {
          const currentPath = location.pathname;
          const targetPath = getRouteForStep(currentStep);
          if (currentPath !== targetPath) {
            navigate(targetPath, { replace: true });
            window.location.reload();
          }
        }
      } catch (err) {
        console.error("Progress fetch error:", err);
      }
    };

    if (!isFetched.current) {
      fetchProgress();
      isFetched.current = true;
    }
  }, [token, refreshAccessToken, navigate, expectedStep, location]);
}

function getRouteForStep(step) {
  switch (step) {
    case "NOT_STARTED":
      return "/dashboard";
    case "CONSENT":
      return "/consent";
    case "PRE_EXPERIMENT":
      return "/pre-experiment";
    case "DOCUMENTATION_INTRO":
      return "/documentation-intro";
    case "PLATFORM_TUTORIAL":
      return "/platform-tutorial";
    case "PENDING":
      return "/experiment";
    case "EXPERIMENT":
      return "/experiment";
    case "DOCUMENTATION_REVIEW":
      return "/documentation-review";
    case "WEBSITE1":
      return "/website1";
    case "WEBSITE2":
      // Website2 disabled for now - redirect players to post-experiment instead
      return "/post-experiment";
    case "POST_EXPERIMENT":
      return "/post-experiment";
    case "FOLLOW_UP":
      return "/follow-up";
    case "END":
      return "/thank-you";
    default:
      return "/dashboard"; // fallback
  }
}

export default useProgressRedirect;
